import { useMemo } from "react"

import { AlertTriangle, Info, Lightbulb, ShieldAlert, XCircle } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Separator } from "@/components/ui/separator"
import { Spinner } from "@/components/ui/spinner"
import { cn } from "@/lib/utils"

export type QAIssue = {
  severity: "high" | "medium" | "low"
  field: string
  message: string
}

export type QAReviewResult = {
  success: boolean
  score: number
  grade: string
  summary: string
  issues: QAIssue[]
  suggestions: string[]
}

type QAReviewDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  isLoading: boolean
  result: QAReviewResult | null
  onRegenerate: () => void
}

function getScoreTextColor(score: number): string {
  if (score >= 80) return "text-success"
  if (score >= 60) return "text-warning"
  return "text-destructive"
}

function getScoreStrokeColor(score: number): string {
  if (score >= 80) return "stroke-success"
  if (score >= 60) return "stroke-warning"
  return "stroke-destructive"
}

function getScoreBgColor(score: number): string {
  if (score >= 80) return "bg-success/15"
  if (score >= 60) return "bg-warning/15"
  return "bg-destructive/15"
}

function getScoreMessage(score: number): string {
  if (score >= 80) return "Excellent quality — ready for review"
  if (score >= 60) return "Good foundation — some improvements needed"
  return "Needs significant work before approval"
}

function ScoreRing({ score, grade }: { score: number; grade: string }) {
  const radius = 52
  const stroke = 8
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (score / 100) * circumference

  const color = getScoreTextColor(score)

  const strokeColor = getScoreStrokeColor(score)

  const bgColor = getScoreBgColor(score)

  return (
    <div className={cn("flex items-center gap-6 rounded-xl p-5", bgColor)}>
      <div className="relative flex shrink-0 items-center justify-center">
        <svg className="-rotate-90" height={128} width={128}>
          <circle
            className="stroke-muted"
            cx={64}
            cy={64}
            fill="none"
            r={radius}
            strokeWidth={stroke}
          />
          <circle
            className={cn(strokeColor, "transition-[stroke,stroke-dashoffset] duration-700")}
            cx={64}
            cy={64}
            fill="none"
            r={radius}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            strokeWidth={stroke}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={cn("text-3xl font-bold tabular-nums", color)}>{score}</span>
          <span className="text-muted-foreground text-xs">/ 100</span>
        </div>
      </div>
      <div className="flex-1">
        <div className="flex items-baseline gap-2">
          <span className={cn("text-4xl font-bold", color)}>{grade}</span>
          <span className="text-muted-foreground text-sm font-medium">Grade</span>
        </div>
        <p className="text-muted-foreground mt-1 text-xs">{getScoreMessage(score)}</p>
      </div>
    </div>
  )
}

const severityConfig = {
  high: {
    icon: XCircle,
    label: "High",
    containerClass: "border-destructive bg-destructive/15",
    iconClass: "text-destructive",
    badgeVariant: "destructive" as const,
  },
  medium: {
    icon: AlertTriangle,
    label: "Medium",
    containerClass: "border-warning bg-warning/15",
    iconClass: "text-warning",
    badgeVariant: "secondary" as const,
  },
  low: {
    icon: Info,
    label: "Low",
    containerClass: "border-primary bg-primary/15",
    iconClass: "text-primary",
    badgeVariant: "outline" as const,
  },
}

function IssueCard({ issue }: { issue: QAIssue }) {
  const config = severityConfig[issue.severity]
  const Icon = config.icon

  return (
    <div className={cn("rounded-lg border p-3", config.containerClass)}>
      <div className="flex items-start gap-2.5">
        <Icon className={cn("mt-0.5 size-4 shrink-0", config.iconClass)} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Badge className="text-[10px]" variant={config.badgeVariant}>
              {config.label}
            </Badge>
            {issue.field && (
              <span className="text-muted-foreground truncate text-xs font-medium">
                {issue.field}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm leading-relaxed">{issue.message}</p>
        </div>
      </div>
    </div>
  )
}

function SuggestionCard({ suggestion, index }: { suggestion: string; index: number }) {
  return (
    <div className="bg-muted/40 flex items-start gap-3 rounded-lg p-3">
      <div className="bg-primary/10 text-primary flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold">
        {index + 1}
      </div>
      <p className="text-sm leading-relaxed">{suggestion}</p>
    </div>
  )
}

export function QAReviewDialog({
  open,
  onOpenChange,
  isLoading,
  result,
  onRegenerate,
}: QAReviewDialogProps) {
  const issuesByPriority = useMemo(() => {
    if (!result?.issues) return { high: [], medium: [], low: [] }
    return {
      high: result.issues.filter((i) => i.severity === "high"),
      medium: result.issues.filter((i) => i.severity === "medium"),
      low: result.issues.filter((i) => i.severity === "low"),
    }
  }, [result?.issues])

  const issueCounts = useMemo(() => {
    if (!result?.issues) return { high: 0, medium: 0, low: 0, total: 0 }
    const high = result.issues.filter((i) => i.severity === "high").length
    const medium = result.issues.filter((i) => i.severity === "medium").length
    const low = result.issues.filter((i) => i.severity === "low").length
    return { high, medium, low, total: high + medium + low }
  }, [result?.issues])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="size-5" />
            QA Review
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {isLoading && (
            <div className="flex flex-col items-center justify-center gap-3 py-16">
              <Spinner className="size-8" />
              <div className="text-center">
                <p className="text-sm font-medium">Analyzing use case...</p>
                <p className="text-muted-foreground mt-1 text-xs">
                  Checking completeness, consistency, and quality
                </p>
              </div>
            </div>
          )}
          {!isLoading && result && (
            <div className="space-y-5">
              {/* Score ring */}
              <ScoreRing grade={result.grade} score={result.score} />

              {/* Summary */}
              <div className="rounded-lg border p-4">
                <p className="text-sm leading-relaxed">{result.summary}</p>
              </div>

              {/* Issue severity bar */}
              {issueCounts.total > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="flex items-center gap-2 text-sm font-semibold">
                      <AlertTriangle className="size-4" />
                      Issues Found
                      <span className="text-muted-foreground font-normal">
                        ({issueCounts.total})
                      </span>
                    </h4>
                    <div className="flex items-center gap-3 text-xs">
                      {issueCounts.high > 0 && (
                        <span className="text-destructive flex items-center gap-1">
                          <span className="border-foreground bg-destructive inline-block size-2 rounded-full border" />
                          {issueCounts.high} high
                        </span>
                      )}
                      {issueCounts.medium > 0 && (
                        <span className="text-warning flex items-center gap-1">
                          <span className="border-foreground bg-warning inline-block size-2 rounded-full border" />
                          {issueCounts.medium} medium
                        </span>
                      )}
                      {issueCounts.low > 0 && (
                        <span className="text-primary flex items-center gap-1">
                          <span className="border-foreground bg-primary inline-block size-2 rounded-full border" />
                          {issueCounts.low} low
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Stacked severity bar */}
                  <div className="bg-muted flex h-2 overflow-hidden rounded-full">
                    {issueCounts.high > 0 && (
                      <div
                        className="border-foreground bg-destructive-ink border transition-[width]"
                        style={{ width: `${(issueCounts.high / issueCounts.total) * 100}%` }}
                      />
                    )}
                    {issueCounts.medium > 0 && (
                      <div
                        className="border-foreground bg-warning-ink border transition-[width]"
                        style={{ width: `${(issueCounts.medium / issueCounts.total) * 100}%` }}
                      />
                    )}
                    {issueCounts.low > 0 && (
                      <div
                        className="border-foreground bg-primary-ink border transition-[width]"
                        style={{ width: `${(issueCounts.low / issueCounts.total) * 100}%` }}
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Issues grouped by severity */}
              {issueCounts.total > 0 && (
                <div className="space-y-2">
                  {[
                    ...issuesByPriority.high,
                    ...issuesByPriority.medium,
                    ...issuesByPriority.low,
                  ].map((issue, i) => (
                    <IssueCard key={i} issue={issue} />
                  ))}
                </div>
              )}

              {/* Suggestions */}
              {result.suggestions.length > 0 && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <h4 className="flex items-center gap-2 text-sm font-semibold">
                      <Lightbulb className="text-warning-ink size-4" />
                      Suggestions
                      <span className="text-muted-foreground font-normal">
                        ({result.suggestions.length})
                      </span>
                    </h4>
                    <div className="space-y-2">
                      {result.suggestions.map((s, i) => (
                        <SuggestionCard key={i} index={i} suggestion={s} />
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button disabled={isLoading} size="sm" variant="outline" onClick={onRegenerate}>
            Re-analyze
          </Button>
          <Button size="sm" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function getBadgeTextColor(score: number): string {
  if (score >= 80) return "text-success"
  if (score >= 60) return "text-warning"
  return "text-destructive"
}

/** Compact inline score display for dashboard cards */
export function QAScoreBadge({
  score,
  grade,
  className,
}: {
  score: number
  grade: string
  className?: string
}) {
  const color = getBadgeTextColor(score)

  const bg = getScoreBgColor(score)

  return (
    <div className={cn("inline-flex items-center gap-1.5 rounded-md px-2 py-1", bg, className)}>
      <span className={cn("text-sm font-bold tabular-nums", color)}>{score}</span>
      <span className={cn("text-xs font-semibold", color)}>{grade}</span>
    </div>
  )
}
